import { AfterViewInit, Component, ComponentRef, ContentChild, ElementRef, Input, OnChanges, OnInit, Sanitizer, SimpleChanges, ViewChild } from "@angular/core";
import { DateRange } from "@enums/DateRange.enum";
import { TimeFilterType } from "@reports/enums/TimeFilterType";
import { DateService } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { Subscription } from "rxjs";
import { IVButtonDropDownItem } from "../../_core/v-button/v-button.component";
import { VCalendarComponent } from "../v-calendar/v-calendar.component";
import { VTimeFiltersComponent } from "../v-time-filters/v-time-filters.component";
import { CalendarService } from "@app/services/calendar.service";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import "./v-date-selector.component.scss";

interface IQuickDate {
    label: string;
    dateRange: DateRange;
    selected?: boolean;
}

interface ITimeRestrictionOption {
    label: string;
    id: number;
}

// Design inspo
// https://dribbble.com/shots/18808665-Trade-history-Untitled-UI

@Component({
    selector: "v-date-selector",
    template: require("./v-date-selector.component.html")
})
export class VDateSelectorComponent extends VCalendarComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() dateRange: DateRange;

    @Input() mobile = false;
    @Input() showQuickDates = true;
    @Input() vertical: boolean;
    @Input() showTimeFilter: boolean;

    @ViewChild(VCalendarComponent) calendar: VCalendarComponent;
    @ContentChild(VTimeFiltersComponent) timeFilters: ComponentRef<VTimeFiltersComponent>;

    // --

    TimeFilterType = TimeFilterType;

    quickDates: IQuickDate[] = [];

    selectedTimeRestrictionOption: IVButtonDropDownItem<ITimeRestrictionOption>;
    timeRestrictionOptions: IVButtonDropDownItem<ITimeRestrictionOption>[] = [];
    showTimeFilterOptions: boolean = false;
    disableTimeFilterToggle: boolean;

    timeFiltersSubscription: Subscription;

    protected defaultTimeFilterStart: string = "08:00";
    protected defaultTimeFilterEnd: string = "17:00";

    // ---

    constructor(
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly veriDate: VeriDate,
        translationService: TranslationService,
        dateService: DateService,
        sanitizer: Sanitizer,
        calendarService: CalendarService) {
        super(sanitizer, dateService, translationService, calendarService);

        this.timeRestrictionOptions.push({ label: "Start & End Time", id: 1 });
        this.timeRestrictionOptions.push({ label: "Time Filter", id: 2 });

        this.selectedTimeRestrictionOption = this.timeRestrictionOptions[0];
    }

    ngOnInit(): void {
        this.createQuickDates();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.createQuickDates();

        // Handy setting for mobile view
        if (changes["mobile"] && this.mobile) {
            this.setMobileView();
        }

        if (changes["dateRange"]) {
            var matchingRange = this.quickDates.find(qd => qd.dateRange == this.dateRange);
            if (matchingRange) {
                this.quickDateClicked(matchingRange);
            }
            else {
                console.warn("Unsupported date range specified");
            }
        }

        if (changes["date"] || changes["endDate"]) {
            this.updateQuickDateSelectionFromDates(new Date(this.date), new Date(this.endDate));
        }
    }

    ngAfterViewInit(): void {

        setTimeout(() => {
            this.autoSetVertical();
        });
    }

    ngAfterContentInit(): void {
        if (this.timeFilters) {
            this.setUpTimeFiltersView();
        }
    }

    ngOnDestroy(): void {
        this.timeFiltersSubscription?.unsubscribe();
    }

    onDateChanged(date: Date): void {
        this.date = this.endDate = date;
        this.dateChanged.emit(date);

        this.updateQuickDateSelectionFromDates(this.date, this.date);
    }

    onDateRangeChanged(dates: Date[]): void {
        this.date = dates[0];
        this.endDate = dates[1];

        this.updateQuickDateSelectionFromDates(this.date, this.endDate);

        this.dateRangeChanged.emit(dates);
    }

    quickDateClicked(quickDate: IQuickDate): void {

        var range = this.veriDate.dateRangeFromEnum(quickDate.dateRange);
        this.date = range.start;
        this.endDate = this._endDate = range.end;

        this.quickDates.forEach(q => q.selected = false);
        quickDate.selected = true;

        this.dateRangeChanged.emit([this.date, this.endDate]);
    }

    timeRestrictionOptionChanged(option: IVButtonDropDownItem<ITimeRestrictionOption>): void {
        this.selectedTimeRestrictionOption = option;
    }

    toggleTimeFilterOptions(): void {
        this.showTimeFilterOptions = !this.showTimeFilterOptions;
    }

    // ---

    private setMobileView(): void {
        this.showQuickDates = false;
        this.monthsToShow = 1;
    }

    private setUpTimeFiltersView(): void {
        this.disableTimeFilterToggle = this.showTimeFilterOptions = this.timeFilters['timeFilterType'] !== TimeFilterType.None;

        this.timeFiltersSubscription = this.timeFilters['timeFilterTypeChange'].subscribe(type => {
            this.disableTimeFilterToggle = type !== TimeFilterType.None;
        })
    }

    private updateQuickDateSelectionFromDates(startDate: Date, endDate?: Date): void {
        if (!this.date) {
            return;
        }

        var dateRange = this.veriDate.dateRangeIntoEnum({ start: new Date(startDate), end: new Date(endDate) });

        this.quickDates.forEach(q => q.selected = q.dateRange == dateRange);
    }

    private createQuickDates(): void {
        if (this.quickDates?.length) {
            return;
        }

        this.quickDates = [];

        if (this.direction == CalendarDirection.TodayFuture || this.direction == CalendarDirection.TodayPast) {
            this.quickDates.push({ label: "", dateRange: DateRange.Today, selected: true });
        }

        this.quickDates.push({ label: "", dateRange: DateRange.Yesterday });

        if (this.maxDays >= 7) {
            this.quickDates.push({ label: "", dateRange: DateRange.Last7Days });
            this.quickDates.push({ label: "", dateRange: DateRange.LastWeek });
        }

        if (this.maxDays >= 31) {
            this.quickDates.push({ label: "", dateRange: DateRange.ThisMonth });
            this.quickDates.push({ label: "", dateRange: DateRange.LastMonth });
        }

        this.quickDates.forEach(qd => {
            qd.label = this.translationService.tEnum(DateRange, "DateRange", qd.dateRange)
        });
    }

    private autoSetVertical(): void {
        if (this.vertical == null) {
            var width = this.elementRef.nativeElement.getBoundingClientRect().width;
            if (width < 350) {
                this.vertical = true;
            }
        }
    }
}
